import React, { useMemo, useContext } from "react";
import { AppStateContext } from "../../containers/App";
import { InputCustomChangeEventHandler } from "../../types";
import { get } from "../../utils/helpers";
import { loadCountries } from "../../utils/api/countries";
import { loadNationalities } from "../../utils/api/nationalities";
import { Place } from "../../types";
import useT from "../../hooks/useT";
import useAsyncRetryMax from "../../hooks/useAsyncRetryMax";
import InputSelectCustom, {
  ReactSelectOption,
  InputSelectCustomProps
} from "../InputSelectCustom";

// @TODO when Omit is available as a built-in TS helper:
// interface InputCountryProps extends Omit<InputSelectCustomProps, "options" | "onChange"> {}
export interface InputCountryProps
  extends Pick<
    InputSelectCustomProps,
    Exclude<keyof InputSelectCustomProps, "options" | "onChange">
  > {
  /**
   * If the input is optional, you can show a "prefer not to say" option which will
   * set the value to an empty string.
   *
   * @default false
   */
  showEmptyOption?: boolean;
  /** Event handler to publish changes to the controlling parent component. */
  onChange: InputCustomChangeEventHandler;
}

interface asyncRetryMax {
  retry: () => void;
  retries: number;
  loading: boolean;
  error?: Error | undefined;
  value?: Place[] | undefined;
}

const HAS_NO_TRANSLATION = "—";

export default function InputCountry({
  name = "",
  searchable = true,
  clearable = true,
  showEmptyOption = false,
  lang,
  ...props
}: InputCountryProps) {
  let type;
  let countries = {} as asyncRetryMax;
  let key: string;

  // Load any label overrides based on the zone
  const [appState] = useContext(AppStateContext);
  const config = appState.config;
  const { zone } = appState;
  const zoneUpperCased = zone.toUpperCase();

  if (name === "addressCountry") {
    countries = useAsyncRetryMax(() => loadCountries(), 3, []);
    key = "geo.addressCountry";
    type = "countries";
  }
  if (name === "nationality") {
    countries = useAsyncRetryMax(() => loadNationalities(), 3, []);
    key = "geo.addressNationality";
    type = "nationalities";
  }

  const { t, i18n } = useT(type);
  const lng = lang || i18n.language;

  const countryLabelOverrides =
    get(config, ["labelOverrides", appState.zone, name, appState.language]) ||
    {};

  const options = useMemo(() => {
    const _options: ReactSelectOption[] = [];

    if (showEmptyOption) {
      _options.push({
        label: t("common:general.fields.notSet.label", { lng }),
        value: ""
      });
    }

    if (countries.value) {
      countries.value.forEach(p => {
        const countryCode = get(p, key) || get(p, "identifier");

        const countryName = t(countryCode, {
          lng,
          defaultValue: HAS_NO_TRANSLATION
        });

        // Override country label if necessary
        const countryLabel = (() => {
          if (countryLabelOverrides[countryCode]) {
            return countryLabelOverrides[countryCode];
          } else {
            return countryName;
          }
        })();

        if (countryName !== HAS_NO_TRANSLATION) {
          _options.push({
            label: countryLabel,
            value: countryCode
          });
        }
      });
    }

    return _options;
  }, [countries.value, lng, clearable]);

  return (
    <InputSelectCustom
      {...props}
      name={name}
      options={options}
      firstPositionOption={zoneUpperCased}
      disabled={props.disabled || countries.loading}
      clearable={clearable}
      searchable={searchable}
      lang={lng}
    />
  );
}
